import React, { useState } from 'react';
import { Row, Col, Typography, Card, Space, Image, Button, Divider } from 'antd';

import QuestionTitle from './QuestionTitle';

import { bucket_url } from '../../../constants/constString';
import { shouldUseKatex } from '../../../constants/constFunction';
import MathInput from '../../Common/MathJax';
import './MCQ.component.style.css';

import QuestionsImage from './QuestionsImage';
import PdfPlaceholder from '../../../assets/images/pdf_placeholder.png';
import MCAInput from './MCAInput';
import ImagePreview from './ImagePreview';
import QuestionBookmark from './QuestionBookmark';
import QuestionFeedback from './QuestionFeedback';

const { Text } = Typography;

const MCA= props => {
	const {
		question,
		questionIndex,
		examId,
		groupId,
		isResult,
		isAssessment,
		examMeta,
		isPracticeQuiz = false,
		onAnswerSelect
	} = props;

	// State for showing answer/explanation in instant practice
	const [showAnswer, setShowAnswer] = useState(false);
	const [showExplanation, setShowExplanation] = useState(false);
	const isInstantPractice = isPracticeQuiz || examMeta?.isInstantPractice;

	const questionTitle = () => {
		let title = '';
		if (isResult) {
			title = question?.questionId?.title || '';
		} else {
			title = question?.title || '';
		}

		return shouldUseKatex(title) >= 0 ? <MathInput mathItem={title} /> : title;
	};

	const handleShowAnswer = () => {
		if (showAnswer) {
			// Toggle off - hide answer
			setShowAnswer(false);
			setShowExplanation(false); // Also hide explanation if it was shown
		} else {
			// Toggle on - show answer
			setShowAnswer(true);
			setShowExplanation(false); // Hide explanation when showing only answer
		}
	};

	const handleShowExplanation = () => {
		if (showExplanation) {
			// Toggle off - hide explanation
			setShowExplanation(false);
			setShowAnswer(false); // Also hide answer
		} else {
			// Toggle on - show explanation (which includes answer)
			setShowAnswer(true);
			setShowExplanation(true);
		}
	};

	// Determine if we should show answer/explanation
	const shouldShowAnswer = isResult || (isInstantPractice && showAnswer);
	const shouldShowExplanation = isResult || (isInstantPractice && showExplanation);

	return (
		<Card 
			bordered={false} 
			className='mcq-question-card'
			style={{ 
				width: '100%',
				borderRadius: '12px',
				boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
				marginBottom: '20px'
			}}
		>
			<div className='question-meta-actions'>
				<QuestionTitle
					title={questionTitle()}
					marks={isResult ? question?.marks || '0' : question?.point}
					questionIndex={questionIndex}
				/>
				<div>
					<QuestionBookmark question={question} examId={examId} />
					<QuestionFeedback question={question} examId={examId} />
				</div>
			</div>
			<Divider style={{ margin: '12px 0' }} />
			{!isAssessment &&
				!isResult &&
				(question?.image || question?.file) &&
				(question?.image?.length > 0 || question?.file?.length > 0) && (
					<QuestionsImage question={question} />
				)}

			{!isAssessment &&
				isResult &&
				(question?.questionId?.image || question?.questionId?.file) &&
				(question?.questionId?.image?.length > 0 ||
					question?.questionId?.file?.length > 0) && (
					<Row style={{ marginTop: 5, marginBottom: 15 }}>
						<Col xs={23} md={23} offset={1}>
							<Space>
								{question?.questionId?.image &&
									question?.questionId?.image.map(item => (
										<Image placeholder src={bucket_url + item} width={'100%'} />
									))}
								{!!question &&
									question?.questionId?.file &&
									question.questionId?.file.map(item => (
										<a
											href={bucket_url + item}
											target='_blank'
											style={{ display: 'inline-block' }}
											rel='noreferrer'
										>
											<Image
												placeholder={<Image src={PdfPlaceholder} />}
												src={bucket_url + item}
												fallback={PdfPlaceholder}
												width={'100%'}
												style={{ objectFit: 'contain' }}
											/>
										</a>
									))}
							</Space>
						</Col>
					</Row>
				)}
			{isAssessment &&
				(question?.question?.image || question?.question?.file) &&
				question?.question?.image?.length > 0 &&
				question?.question?.file?.length > 0 && (
					<Row style={{ marginTop: 5, marginBottom: 15 }}>
						<Col xs={23} md={23} offset={1}>
							<Space>
								{question?.question?.image &&
									question?.question?.image.map(item => (
										<Image placeholder src={bucket_url + item} width={'100%'} />
									))}
								{!!question &&
									question?.question?.file &&
									question.question?.file.map(item => (
										<a
											href={bucket_url + item}
											target='_blank'
											rel='noreferrer'
										>
											<Image
												alt='PDF File'
												placeholder={<Image src={PdfPlaceholder} />}
												src={bucket_url + item}
												fallback={PdfPlaceholder}
												width={'100%'}
												style={{ objectFit: 'contain' }}
											/>
										</a>
									))}
							</Space>
						</Col>
					</Row>
				)}
			<Row>
				<MCAInput
					isResult={isResult}
					examId={examId}
					groupId={groupId}
					question={question}
					examMeta={examMeta}
					isPracticeQuiz={isPracticeQuiz}
					onAnswerSelect={(answer) => {
						if (onAnswerSelect) {
							onAnswerSelect(answer);
						}
					}}
					showAnswer={shouldShowAnswer}
				/>

				{/* Explanation & Notes Section Start */}
				{shouldShowExplanation &&
					(question?.questionId?.explanation ||
						question?.questionId?.explanationExt) && (
						<Col xs={24} md={24} style={{ marginTop: 16 }}>
							<Card 
								className='explanation-card' 
								bordered
								style={{
									background: '#f8f9fa',
									borderRadius: '8px',
									border: '1px solid #e9ecef'
								}}
							>
								<Text strong style={{ fontSize: '16px', color: '#1890ff' }}>Explanation</Text>

								<p style={{ marginBottom: 0, marginTop: 8 }}>
									{question?.questionId?.explanation &&
									shouldUseKatex(question?.questionId?.explanation) >= 0 ? (
										<MathInput mathItem={question?.questionId?.explanation} />
									) : (
										question?.questionId?.explanation
									)}
								</p>
								<Row>
									<Col xs={24} md={24}>
										<ImagePreview
											question={question}
											bucket_url={bucket_url}
											PdfPlaceholder={PdfPlaceholder}
										/>
									</Col>
								</Row>
							</Card>
						</Col>
					)}
				{/* Explanation & Notes Section end */}
			</Row>

			{/* Show Answer & Explanation Buttons for Instant Practice */}
			{isInstantPractice && !isResult && (
				<>
					<Divider style={{ margin: '16px 0', borderColor: '#e8e8e8' }} />
					<Row justify="space-between" align="middle" style={{ marginTop: 8, padding: '0 4px' }}>
						<Col>
							<Button
								type="default"
								onClick={handleShowAnswer}
								className={`show-answer-btn ${showAnswer && !showExplanation ? 'active' : ''}`}
								style={{
									borderRadius: '6px',
									fontWeight: 500,
									padding: '6px 16px',
									height: 'auto',
									fontSize: '14px',
									borderColor: showAnswer && !showExplanation ? '#1890ff' : '#d9d9d9',
									color: showAnswer && !showExplanation ? '#1890ff' : '#262626',
									background: 'transparent'
								}}
							>
								Show Answer
							</Button>
						</Col>
						<Col>
							<Button
								type="default"
								onClick={handleShowExplanation}
								className={`show-explanation-btn ${showExplanation ? 'active' : ''}`}
								style={{
									borderRadius: '6px',
									fontWeight: 500,
									padding: '6px 16px',
									height: 'auto',
									fontSize: '14px',
									borderColor: showExplanation ? '#1890ff' : '#d9d9d9',
									color: showExplanation ? '#1890ff' : '#262626',
									background: 'transparent'
								}}
							>
								Show Explanation
							</Button>
						</Col>
					</Row>
				</>
			)}
		</Card>
	);
};

export default MCA;
